import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatPrice, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const categories = ["Detergent rufe albe", "Detergent rufe colorate", "Detergent rufe negre", "Balsam de rufe"];

const emptyForm = {
  name: "", category: categories[0], fragrance: "", price: "", old_price: "",
  description: "", long_description: "", image: "", features: "", in_stock: true, featured: false,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => api.get("/products").then((res) => setProducts(res.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      old_price: p.old_price ?? "",
      fragrance: p.fragrance ?? "",
      features: (p.features || []).join(", "),
    });
    setOpen(true);
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.price || !form.image || !form.description) {
      toast.error("Completează numele, prețul, imaginea și descrierea");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      fragrance: form.fragrance || null,
      price: parseFloat(form.price),
      old_price: form.old_price ? parseFloat(form.old_price) : null,
      description: form.description,
      long_description: form.long_description || "",
      image: form.image,
      features: form.features ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : [],
      in_stock: form.in_stock,
      featured: form.featured,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produs actualizat" : "Produs adăugat");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Produs șters");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Produse</h1>
          <p className="mt-1 text-slate-500">{products.length} produse în catalog</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-sky-600 hover:bg-sky-500" data-testid="admin-add-product">
          <Plus className="mr-1.5 h-4 w-4" /> Adaugă produs
        </Button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Produs</th>
              <th className="hidden px-5 py-3 font-semibold md:table-cell">Categorie</th>
              <th className="px-5 py-3 font-semibold">Preț</th>
              <th className="hidden px-5 py-3 font-semibold sm:table-cell">Stoc</th>
              <th className="px-5 py-3 text-right font-semibold">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {products.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-10 w-10 rounded-lg bg-sky-50 object-contain p-1" />
                    <span className="font-semibold text-slate-900">{p.name}</span>
                  </div>
                </td>
                <td className="hidden px-5 py-3 text-slate-600 md:table-cell">{p.category}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{formatPrice(p.price)}</td>
                <td className="hidden px-5 py-3 sm:table-cell">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${p.in_stock ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {p.in_stock ? "În stoc" : "Indisponibil"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-sky-50 hover:text-sky-600" data-testid={`admin-edit-${p.slug}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleteId(p.id)} className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`admin-delete-${p.slug}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit/Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editează produs" : "Adaugă produs"}</DialogTitle>
            <DialogDescription>Completează informațiile produsului.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nume *</Label>
              <Input value={form.name} onChange={update("name")} className="mt-1.5" data-testid="product-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="product-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Parfum</Label>
                <Input value={form.fragrance} onChange={update("fragrance")} className="mt-1.5" data-testid="product-form-fragrance" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preț (lei) *</Label>
                <Input type="number" value={form.price} onChange={update("price")} className="mt-1.5" data-testid="product-form-price" />
              </div>
              <div>
                <Label>Preț vechi (opțional)</Label>
                <Input type="number" value={form.old_price} onChange={update("old_price")} className="mt-1.5" data-testid="product-form-oldprice" />
              </div>
            </div>
            <div>
              <Label>Imagine produs *</Label>
              <div className="mt-1.5">
                <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testId="product-image-upload" />
              </div>
            </div>
            <div>
              <Label>Descriere scurtă *</Label>
              <Textarea value={form.description} onChange={update("description")} className="mt-1.5" rows={2} data-testid="product-form-description" />
            </div>
            <div>
              <Label>Descriere lungă</Label>
              <Textarea value={form.long_description} onChange={update("long_description")} className="mt-1.5" rows={3} data-testid="product-form-longdesc" />
            </div>
            <div>
              <Label>Caracteristici (separate prin virgulă)</Label>
              <Input value={form.features} onChange={update("features")} className="mt-1.5" data-testid="product-form-features" />
            </div>
            <div className="flex gap-8">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Switch checked={form.in_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, in_stock: v }))} data-testid="product-form-instock" /> În stoc
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="product-form-featured" /> Recomandat
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-500" data-testid="product-form-save">
              {saving ? "Se salvează..." : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi produsul?</AlertDialogTitle>
            <AlertDialogDescription>Această acțiune nu poate fi anulată.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete-product">Șterge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
